import React, { Fragment, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
    IconClose,
    IconLoading,
    IconRefresh,
} from "~/components/Iconfont/Iconfont";
import { TradeOrder } from "~/types";
import TradeOrderTokenBadge from "../components/TradeOrderTokenBadge";
import { formatDate } from "~/utils";
import {
    formatAmount,
    formatPercent,
    formatPrice,
} from "~/utils/number-format";
import { fetchOrderDetail, fetchTokenMarket } from "~/api/endpoints";
import clsx from "clsx";
import { ORDER_DIRECTION, ORDER_STATUS } from "~/pages/constants";
import { useTranslation } from "react-i18next";
import { Button, Image } from "antd-mobile";
import useScrollTop from "~/hook/useScrollTop";
import LottieSticker from "~/components/LottieSticker";
import { useAppDispatch } from "~/store";
import { updateToken } from "~/store/token";
import useAsyncAction from "~/hook/useAsyncAction";

type TradeHistoryItemProps = {
    order: TradeOrder;
    onReverseTrade: (order: TradeOrder) => void;
};

const TradeHistoryItem: React.FC<TradeHistoryItemProps> = ({
    order: passedOrder,
    onReverseTrade,
}) => {
    const [order, setOrder] = useState(passedOrder);
    const scrollToTop = useScrollTop();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const chain_id = useMemo(() => {
        if (order.direction === ORDER_DIRECTION.buy) {
            return order.to_token.chain_id;
        } else {
            return order.from_token.chain_id;
        }
    }, [order]);

    const contract = useMemo(() => {
        if (order.direction === ORDER_DIRECTION.buy) {
            return order.to_token.contract;
        } else {
            return order.from_token.contract;
        }
    }, [order]);

    const handleReTrade = (e: React.MouseEvent) => {
        e.stopPropagation();
        onReverseTrade(order);
        scrollToTop();
    };

    const shareActionFn = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();

            return fetchTokenMarket(String(chain_id), contract)
                .then((res) => {
                    const token = res.data;
                    dispatch(
                        updateToken({
                            token_base: token.token_base,
                            token_market: {
                                ...token.token_market,
                                buy_price: order.uint_price,
                                buy_change: order.income.change,
                            },
                            share_type: "order",
                        })
                    );
                    navigate("/token-share");
                })
                .catch((e) => {
                    console.error(e);
                });
        },
        [order, chain_id, contract]
    );

    const shareAction = useAsyncAction(shareActionFn);

    const refreshActionFn = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            return fetchOrderDetail(order.order_id).then((res) => {
                const detail = res.data.detail;
                setOrder((order) => {
                    return {
                        ...order,
                        income: detail.income,
                        status: detail.status,
                    };
                });
            });
        },
        [order]
    );

    const refreshAction = useAsyncAction(refreshActionFn);

    const mertricColumns = [
        {
            key: (
                <div className="flex items-center">
                    <span className="mr-1">{t("income")}</span>
                    <IconRefresh
                        className={clsx(
                            refreshAction.isLoading && "loading-rotate-1s",
                            "text-text_2 text-body1"
                        )}
                    />
                </div>
            ),
            value: <TradeOrderIncome order={order} />,
            onClick: refreshAction.run,
        },
        { key: t("unit_price"), value: formatPrice(order.uint_price) },
        {
            key: `${t("pay")} (${order.from_token.symbol.toUpperCase()})`,
            value: (
                <>
                    <Image
                        src={order.from_token.logo}
                        className="w-[16px] h-[16px] rounded-lg mr-[4px]"
                    />
                    <span>{formatAmount(order.from_token.amount)}</span>
                </>
            ),
        },
        {
            key: `${t("get")} (${order.to_token.symbol.toUpperCase()})`,
            value: (
                <>
                    <Image
                        src={order.to_token.logo}
                        className="w-[16px] h-[16px] rounded-lg mr-[4px]"
                    />
                    <span>{formatAmount(order.to_token.amount)}</span>
                </>
            ),
        },
        { key: t("time"), value: formatDate(order.create_time, false) },
        { key: t("status"), value: <TradeOrderStatus order={order} /> },
    ];

    return (
        <Fragment>
            <div
                className="flex flex-col justify-between px-4 pt-3 pb-[18px] border-b-[0.5px] border-border_2 cursor-pointer"
                onClick={() =>
                    navigate(
                        `/trade-detail?order_id=${order.order_id}&chain_id=${chain_id}&contract=${contract}`
                    )
                }
            >
                <div className="flex items-center justify-between">
                    <TradeOrderTokenBadge order={order} />
                    <div className="flex items-center">
                        <Button
                            color="primary"
                            fill="solid"
                            style={
                                {
                                    height: "2rem",
                                    padding: "0px 12px",
                                    "--border-radius": "8px",
                                    "--border-color": "transparent",
                                    "--text-color": "var(--color_text_1)",
                                    "--background-color": "var(--color_bg_1)",
                                } as React.CSSProperties
                            }
                            onClick={handleReTrade}
                        >
                            <span className="text-body4 font-semibold">
                                {t(
                                    order.direction === ORDER_DIRECTION.buy
                                        ? "sell_now"
                                        : "buy_now"
                                )}
                            </span>
                        </Button>
                        <div
                            className="w-[32px] h-[32px] bg-AlphaOrange dark:bg-DarkAlphaOrange cursor-pointer rounded-lg flex items-center justify-center ml-1"
                            onClick={shareAction.run}
                        >
                            {shareAction.isLoading ? (
                                <IconLoading className="text-h5 text-text_2" />
                            ) : (
                                <LottieSticker
                                    lottieName="hand-coin-orange"
                                    className="w-5 h-5"
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap">
                    {mertricColumns.map((item, index) => {
                        return (
                            <div
                                className="odd:w-3/5 odd:pr-2 even:w-2/5 mt-4"
                                key={index}
                                onClick={(e) => item.onClick?.(e)}
                            >
                                <div className="text-text_2 text-body4 font-Regular">
                                    {item.key}
                                </div>
                                <div className="text-text_1 text-body4 font-semibold mt-1 flex items-center">
                                    {item.value}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Fragment>
    );
};

const TradeOrderIncome: React.FC<{
    order: TradeOrder;
}> = ({ order }) => {
    return (
        <div className="flex items-center">
            <span
                className={clsx(
                    Number(order.income.value) === 0
                        ? "text-text_3"
                        : Number(order.income.value) > 0
                          ? "text-text_buy"
                          : "text-text_sell"
                )}
            >
                {Number(order.income.value) > 0 ? "+" : ""}
                {formatPrice(order.income.value)}
            </span>{" "}
            <span
                className={clsx(
                    Number(order.income.value) === 0
                        ? "text-text_3"
                        : Number(order.income.value) > 0
                          ? "text-text_buy"
                          : "text-text_sell"
                )}
            >
                ({formatPercent(order.income.change)})
            </span>
        </div>
    );
};

const TradeOrderStatus: React.FC<{ order: TradeOrder }> = ({ order }) => {
    const state = order.status;

    const { t } = useTranslation();

    // 映射状态与对应的颜色和文本
    const stateMap: {
        [key in TradeOrder["status"]]: {
            color?: string;
            icon?: JSX.Element;
            text: string;
        };
    } = {
        [ORDER_STATUS.PROGRESS]: {
            icon: <IconLoading className="text-text_3 text-body1" />,
            text: t("in_progress"),
        },
        [ORDER_STATUS.COMPLETED]: {
            color: "bg-green-500",
            text: t("completed"),
        },
        [ORDER_STATUS.FAILED]: {
            icon: <IconClose className="text-text_sell text-body1" />,
            text: t("failed"),
        },
    };

    // 如果状态无效，返回 null
    if (!(state in stateMap)) return null;

    // 提取颜色或图标和文本
    const { color, text, icon } = stateMap[state];

    return (
        <>
            {icon ? (
                <span className="w-4 h-4 flex items-center mr-1">{icon}</span>
            ) : (
                <span className={`w-1.5 h-1.5 rounded-full ${color} mr-1`} />
            )}
            <span className="h-4 flex items-center">{text}</span>
        </>
    );
};

export default React.memo(TradeHistoryItem);
